import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  ShieldCheck,
  Headphones,
  Zap,
  KeyRound,
  CheckCircle2
} from 'lucide-react';

import { loginCustomer, registerCustomer, verifySignupOTP } from '../services/api';

const UserLogin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('signin'); // 'signin' | 'signup'
  const [step, setStep] = useState('form'); // 'form' | 'verify-otp'
  const [showPassword, setShowPassword] = useState(false);

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMsg('');
    setLoading(true);

    try {
      if (!email || !password) {
        throw new Error('Please fill in both email and password.');
      }

      let res;
      if (activeTab === 'signup') {
        if (!name) throw new Error('Please enter your full name.');
        res = await registerCustomer(name, email, password);
      } else {
        res = await loginCustomer(email, password);
      }

      // Check if OTP Verification is Required (New Signup or Unverified Login)
      if (res.requiresOTP) {
        setInfoMsg(res.message || 'Please enter the 6-digit OTP code sent to your email.');
        setStep('verify-otp');
        return;
      }

      if (!res.success) {
        throw new Error(res.message || 'Authentication failed. Please try again.');
      }

      // Save User Session into localStorage
      localStorage.setItem('userToken', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));

      // Redirect to Storefront Home
      navigate('/');
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!otp) throw new Error('Please enter the 6-digit OTP code.');
      const res = await verifySignupOTP(email, otp);

      if (!res.success) {
        throw new Error(res.message || 'Invalid or expired OTP code.');
      }

      // Save User Session into localStorage
      localStorage.setItem('userToken', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));

      // Redirect to Storefront Home
      navigate('/');
      window.location.reload();
    } catch (err) {
      setError(err.message || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', flexDirection: 'column' }}>
      
      {/* MAIN TOP SECTION (SPLIT SCREEN LEFT STAGE + RIGHT CARD) */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', minHeight: 'calc(100vh - 80px)', width: '100%', boxSizing: 'border-box' }}>
        
        {/* LEFT BRAND STAGE */}
        <div style={{
          background: '#ffffff',
          padding: '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderRight: '1px solid #e2e8f0',
          boxSizing: 'border-box'
        }}>
          
          {/* Ambient Red Waves Graphic Background Overlay */}
          <div style={{
            position: 'absolute',
            right: '-10%',
            top: '20%',
            width: '550px',
            height: '550px',
            backgroundImage: `radial-gradient(circle, rgba(254, 226, 226, 0.7) 0%, rgba(255, 255, 255, 0) 70%)`,
            pointerEvents: 'none',
            zIndex: 1
          }}></div>

          <div style={{ position: 'relative', zIndex: 2, maxWidth: '480px', margin: '0 auto' }}>
            
            {/* Subhead & Headline */}
            <span style={{ fontSize: '11px', fontWeight: 900, letterSpacing: '0.15em', color: '#dc2626', textTransform: 'uppercase', display: 'block', marginBottom: '12px' }}>
              WELCOME TO INFRAEDGE
            </span>

            <h1 style={{ fontSize: '40px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: '20px', wordBreak: 'break-word' }}>
              Better Health.<br />
              <span style={{ color: '#dc2626' }}>Brighter</span> You.
            </h1>

            <p style={{ fontSize: '14.5px', color: '#64748b', lineHeight: 1.6, maxWidth: '420px', marginBottom: '32px' }}>
              Advanced red light therapy solutions designed to help you recover faster, feel better, and live your best life.
            </p>

            {/* Left 3 Benefit Badges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '360px' }}>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Zap size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Clinically Proven Technology</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>Trusted by athletes, wellness experts and thousands of happy customers.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>2 Year Warranty</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>Peace of mind with every purchase.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Headphones size={18} />
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Dedicated Support</h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>We're here to help you on your wellness journey.</p>
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* RIGHT LOGIN CARD CONTAINER */}
        <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box' }}>
          
          <div style={{ width: '100%', maxWidth: '440px' }}>

            {/* LOGIN CARD */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '32px 24px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', boxSizing: 'border-box' }}>
              
              {/* STEP 1: FORM INPUTS */}
              {step === 'form' ? (
                <>
                  {/* Tab Switcher: Sign In vs Create Account */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e2e8f0', marginBottom: '28px' }}>
                    <button
                      onClick={() => { setActiveTab('signin'); setError(''); }}
                      style={{
                        padding: '12px',
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'signin' ? '2px solid #dc2626' : '2px solid transparent',
                        color: activeTab === 'signin' ? '#dc2626' : '#64748b',
                        fontSize: '15px',
                        fontWeight: 800,
                        cursor: 'pointer'
                      }}
                    >
                      Sign In
                    </button>

                    <button
                      onClick={() => { setActiveTab('signup'); setError(''); }}
                      style={{
                        padding: '12px',
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'signup' ? '2px solid #dc2626' : '2px solid transparent',
                        color: activeTab === 'signup' ? '#dc2626' : '#64748b',
                        fontSize: '15px',
                        fontWeight: 800,
                        cursor: 'pointer'
                      }}
                    >
                      Create Account
                    </button>
                  </div>

                  {/* Title & Subtitle */}
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>
                      {activeTab === 'signin' ? 'Welcome back!' : 'Join InfraEdge'}
                    </h2>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                      {activeTab === 'signin' ? 'Sign in to continue your wellness journey.' : 'Create an account to manage orders and track wellness progress.'}
                    </p>
                  </div>

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '18px', fontSize: '12.5px', fontWeight: 700 }}>
                      {error}
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    
                    {activeTab === 'signup' && (
                      <div>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            required
                            placeholder="Full name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                          />
                          <User size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                        </div>
                      </div>
                    )}

                    {/* Email Address */}
                    <div>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="email"
                          required
                          placeholder="Email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                        />
                        <Mail size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          style={{ width: '100%', padding: '12px 42px 12px 42px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                        />
                        <Lock size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                        
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* Remember Me & Forgot Password Row */}
                    {activeTab === 'signin' && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12.5px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#475569', fontWeight: 600 }}>
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            style={{ accentColor: '#dc2626', width: '16px', height: '16px' }}
                          />
                          <span>Remember me</span>
                        </label>

                        <Link to="/forgot-password" style={{ color: '#dc2626', textDecoration: 'none', fontWeight: 700 }}>
                          Forgot password?
                        </Link>
                      </div>
                    )}

                    {/* Full Width Red Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '13px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        marginTop: '6px',
                        boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                      }}
                    >
                      {loading ? 'Processing...' : (activeTab === 'signin' ? 'Sign In' : 'Create Account')}
                    </button>

                  </form>
                </>
              ) : (
                
                /* STEP 2: OTP VERIFICATION */
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <KeyRound size={28} />
                    </div>
                    <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>Verify Your Email</h2>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                      Enter the 6-digit OTP code sent to <strong style={{ color: '#0f172a' }}>{email}</strong>
                    </p>
                  </div>

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: '10px', marginBottom: '18px', fontSize: '12.5px', fontWeight: 700 }}>
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleVerifyOTP} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', textAlign: 'center' }}>6-Digit OTP Code</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 984012"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '16px', fontWeight: 900, textAlign: 'center', letterSpacing: '0.1em', outline: 'none' }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '13px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                      }}
                    >
                      {loading ? 'Verifying...' : 'Verify & Complete Login'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setStep('form')}
                      style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: 700, cursor: 'pointer', marginTop: '4px' }}
                    >
                      ← Back to Form
                    </button>
                  </form>
                </div>
              )}

              {/* Security Data Box */}
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '14px', marginTop: '24px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={16} />
                </div>
                <div>
                  <h4 style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Your data is safe with us</h4>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>We use industry-standard encryption to protect your personal information.</p>
                </div>
              </div>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default UserLogin;
